import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Separator } from "@/components/ui/separator";
import {
  Beaker, Layers, MapPin, Phone, Mail, FileCheck, Menu, X,
  ChevronRight, ArrowRight, Loader2, RefreshCw, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_WHITE_URL = "/StrataLogo.svg";
const RHOMBS_ICON_URL = "/STRATA_rombs.svg";

// Language Context
const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);

// Brand colors from logo JSON spec
const BRAND = {
  primary: "#045992",
  primaryHover: "#034B7B",
};

const FALLBACK_IMAGES = {
  hero: "/hero-road.jpg",
  about: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/5o4htdjj_b026-05-72dpi.jpg",
  gallery1: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/128abhk6_rd-scaled.jpg",
  gallery2: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/u8qlaj74_liquid-labs-img4.jpg",
  gallery3: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/0zlq5mn5_2021_CE-asphalt-Ghabchi_001.jpg",
  gallery4: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/y21lv7wr_asphalt-technology.jpg.webp",
  gallery5: "https://customer-assets.emergentagent.com/job_80354d4a-e8c9-4128-a594-d77a39cdc230/artifacts/5o4htdjj_b026-05-72dpi.jpg",
};

// Translations
const TRANSLATIONS = {
  lv: {
    nav: {
      about: "Par mums",
      methods: "Metodes",
      services: "Pakalpojumi",
      laboratory: "Laboratorija",
      contact: "Kontakti",
    },
    hero: {
      title: "Neatkarīga akreditēta laboratorija",
      subtitle: "Precīzi un uzticami būvmateriālu un ceļu būves materiālu testēšanas rezultāti",
      cta: "Uzzināt vairāk",
      contact: "Sazināties",
    },
    about: {
      label: "Par mums",
      title: "SIA \"STRATA\" ir neatkarīga, akreditēta testēšanas laboratorija, kas specializējas būvmateriālu un ceļu būves materiālu pārbaudēs.",
      titleFaded: "Mēs piedāvājam precīzus un uzticamus testēšanas rezultātus, kas palīdz novērtēt materiālu kvalitāti un atbilstību standartiem.",
      cta: "Uzzināt vairāk",
      labTitle: "Laboratorija:",
      labItem1: "Noņem un testē izejmateriālu paraugus",
      labItem2: "Pārbauda asfaltbetona kvalitātes rādītājus",
      labItem3: "Pārbauda un testē virsmas apstrādes tehnoloģijas",
      labItem4: "Testē grunts un ceļu būves konstruktīvo kārtu biezumu, sablīvējumu, nestspēju u.c. fizikāli mehāniskos parametrus",
      materials: { sand: "Smilts", gravel: "Šķembas", minerals: "Minerālie materiāli", bitumen: "Bitumens" },
      stats: "dažādu testu veidu",
      statsDesc: "Nodrošinot plašu un kompleksu pakalpojumu klāstu",
      locations: "Četras pakalpojumu sniegšanas vietas:",
      locationsDesc: "Tas ļauj kvalitatīvi apkalpot klientus dažādos reģionos, nodrošinot ātru paraugu apriti un testēšanas procesu.",
      valuesTitle: "Mūsu pamatvērtības:",
      value1: "Neatkarība un objektivitāte",
      value2: "Augsti kvalificēta, pieredzējusi speciālistu komanda",
      value3: "Mūsdienīgs aprīkojums",
      value4: "Precizitāte, procesu izsekojamība un atbildība par katru rezultātu",
    },
    quality: {
      label: "Kvalitātes politika",
      title: "Augstākie standardi",
      text1: "Mūsu darbība balstās uz profesionalitāti, objektivitāti un augstiem kvalitātes kritērijiem. Testēšanu veicam saskaņā ar Latvijas un Eiropas normatīvajām prasībām.",
      text2: "Mūsu mērķis ir sniegt klientiem pārliecību par izmantoto materiālu kvalitāti un drošību būvniecības procesā.",
      card1Title: "Profesionāla kompetence",
      card1Text: "Laboratorija pastāvīgi uzlabo savu darbību un veicina profesionālo kompetenci visos darbības līmeņos.",
      card2Title: "Objektivitāte un neatkarība",
      card2Text: "STRATA nodrošina objektivitāti, neatkarību un konfidencialitāti visos testēšanas procesos.",
      card3Title: "Nepārtraukta pilnveidošana",
      card3Text: "Visi darbinieki ir iesaistīti kvalitātes vadības sistēmas ieviešanā un nepārtrauktā pilnveidošanā.",
    },
    methods: {
      title: "Testēšanas metodes",
      link: "Akreditētas testēšanas metodes (F54-00, 3.11.2025) Aktualizēts 18.02.2026",
      accreditationBtn: "Akreditācijas apliecība",
      tabAccredited: "Akreditētas metodes",
      tabNonAccredited: "Neakreditētas metodes",
      categories: {
        asphalt: "Asfaltbetoni",
        minerals: "Minerālmateriāli",
        field: "Mērījumi objektā",
        bitumen: "Bitumens",
      },
      subcategories: {
        asphaltMix: "Asfaltbetona maisījumi",
        coreTest: "Izurbto paraugu testēšana",
        fineAgg: "Smalko minerālmateriālu testēšana (smilts, izsijas)",
        coarseAgg: "Rupjo minerālmateriālu testēšana (šķembas, maisījumi)",
      },
    },
    services: {
      label: "Pakalpojumi",
      title: "Pakalpojumu pieteikšana",
      text1: "\"STRATA neatkarīga akreditēta laboratorija\" SIA sniedz ceļu būvmateriālu testēšanas pakalpojumus. Laboratorija regulāri pārskata piedāvāto pakalpojumu klāstu.",
      text2: "Piedāvājam iespēju klientiem pašiem nogādāt paraugus laboratorijā vai pieteikt mūsu speciālistu izbraukumu uz objektiem Baltijas valstu reģionā.",
      delivery: "Paraugu nogādāšana",
      deliveryDesc: "Klienti var pašiem nogādāt paraugus laboratorijā un aizpildīt testēšanas pieprasījumu formu.",
      onsite: "Speciālistu izbraukums",
      onsiteDesc: "Pieteikt mūsu speciālistu izbraukumu uz objektiem Baltijas valstu reģionā.",
      guarantee: "Garantijas vēstules nosūtīšana",
      labsLabel: "Sazinies",
      labsTitle: "Mūsu laboratorijas",
      accredited: "Akreditētās",
      nonAccredited: "Neakreditētās",
      addresses: {
        vangazi: "Meža miers, Inčukalna pag., Siguldas nov., LV-2136",
        daugavpils: "Dunduru iela 7u, Daugavpils, LV-5404",
        iecava: "\"Sliedes\" Iecavas pag., Bauskas nov.",
        limbazi: "Mehanizācijas iela 3A",
      },
    },
    contact: {
      label: "Kontakti",
      title: "Sazinies ar mums",
      details: "Rekvizīti",
      companyName: "\"STRATA neatkarīga akreditēta laboratorija\" SIA",
      regNo: "Reģ. Nr.",
      address: "Saulkrastu nov., Sējas pag., Gāršmuiža, \"Stūrnieki\"",
      bank: "Banka:",
      account: "Konts:",
      cookies: "Sīkdatņu politika",
      privacy: "Privātuma politika",
      rights: "Visas tiesības aizsargātas.",
    },
  },
  en: {
    nav: {
      about: "About Us",
      methods: "Methods",
      services: "Services",
      laboratory: "Laboratory",
      contact: "Contact",
    },
    hero: {
      title: "Independent Accredited Laboratory",
      subtitle: "Precise and Reliable Testing Results for Construction and Road Construction Materials",
      cta: "Learn More",
      contact: "Contact Us",
    },
    about: {
      label: "About Us",
      title: "\"STRATA neatkarīga akreditēta laboratorija\" Ltd. is an independent accredited testing laboratory specializing in the testing of construction and road construction materials.",
      titleFaded: "We provide precise and reliable testing results that help assess material quality and compliance with applicable standards.",
      cta: "Learn More",
      labTitle: "Laboratory:",
      labItem1: "Sampling and testing of raw materials",
      labItem2: "Testing of asphalt mixture quality parameters",
      labItem3: "Inspection and testing of surface treatment technologies",
      labItem4: "Testing of soil and road construction structural layers, including thickness, compaction, load-bearing capacity, and other physical and mechanical properties",
      materials: { sand: "Sand", gravel: "Crushed stone", minerals: "Mineral materials", bitumen: "Bitumen" },
      stats: "different types of tests",
      statsDesc: "Providing a wide and comprehensive range of services",
      locations: "We Have Four Service Locations:",
      locationsDesc: "This allows us to provide high-quality services to clients across different regions, ensuring fast sample handling and efficient testing processes.",
      valuesTitle: "Our Core Values:",
      value1: "Independence and objectivity",
      value2: "A highly qualified and experienced team of specialists",
      value3: "Modern equipment and technologies",
      value4: "Precision, process traceability, and responsibility for every result",
    },
    quality: {
      label: "Quality Policy",
      title: "Highest Standards",
      text1: "Our operations are based on professionalism, objectivity, and high-quality standards. We perform testing in accordance with Latvian and European regulatory requirements.",
      text2: "Our goal is to provide clients with confidence in the quality and safety of the materials used in the construction process.",
      card1Title: "Professional Competence",
      card1Text: "The laboratory continuously improves its operations and promotes professional competence at all levels of activity.",
      card2Title: "Objectivity and Independence",
      card2Text: "STRATA ensures objectivity, independence, and confidentiality throughout all testing processes.",
      card3Title: "Continuous Improvement",
      card3Text: "All employees are involved in the implementation and continuous improvement of the quality management system.",
    },
    methods: {
      title: "Testing Methods",
      link: "Accredited Testing Methods (F54-00, 3 November 2025) Updated: 18 February 2026",
      accreditationBtn: "Accreditation Certificate",
      tabAccredited: "Accredited Methods",
      tabNonAccredited: "Non-Accredited Methods",
      categories: {
        asphalt: "Asphalt Mixtures",
        minerals: "Mineral Materials",
        field: "Field Measurements",
        bitumen: "Bitumen",
      },
      subcategories: {
        asphaltMix: "Asphalt Mixtures",
        coreTest: "Testing of Core Samples",
        fineAgg: "Testing of Fine Aggregates (Sand, Screenings)",
        coarseAgg: "Testing of Coarse Aggregates (Crushed Stone and Aggregate Mixtures)",
      },
    },
    services: {
      label: "Services",
      title: "Service Request",
      text1: "\"STRATA neatkarīga akreditēta laboratorija\" Ltd. provides testing services for road construction materials. The laboratory regularly reviews and expands its range of services.",
      text2: "We offer clients the option to deliver samples directly to the laboratory or request on-site visits by our specialists throughout the Baltic region.",
      delivery: "Sample Delivery",
      deliveryDesc: "Clients may deliver samples directly to the laboratory and complete a testing request form.",
      onsite: "On-Site Visit by Our Specialists",
      onsiteDesc: "Request an on-site visit by our specialists anywhere in the Baltic region.",
      guarantee: "Submission of a Letter of Guarantee",
      labsLabel: "Contact Us",
      labsTitle: "Our Laboratories",
      accredited: "Accredited",
      nonAccredited: "Non-Accredited",
      addresses: {
        vangazi: "Meža miers, Inčukalns Parish, Sigulda Municipality, LV-2136, Latvia",
        daugavpils: "7u Dunduru Street, Daugavpils, LV-5404, Latvia",
        iecava: "\"Sliedes\", Iecava Parish, Bauska Municipality, Latvia",
        limbazi: "3A Mehanizācijas Street, Limbaži, Latvia",
      },
    },
    contact: {
      label: "Contact",
      title: "Contact Us",
      details: "Company Details",
      companyName: "\"STRATA neatkarīga akreditēta laboratorija\" Ltd.",
      regNo: "Registration No.",
      address: "Saulkrastu Municipality, Sēja Parish, Gāršmuiža, \"Stūrnieki\"",
      bank: "Bank:",
      account: "Account No.:",
      cookies: "Cookie Policy",
      privacy: "Privacy Policy",
      rights: "All rights reserved.",
    },
  },
};

// Methods data with translations
const METHODS_DATA = {
  accredited: {
    lv: {
      asfaltbetoni: {
        title: "Asfaltbetoni",
        subcategories: [
          {
            title: "Asfaltbetona maisījumi",
            items: [
              { name: "Paraugu noņemšana objektā", standard: "LVS EN 12697-27:2017, p.4.1.,p. 4.3." },
              { name: "Paraugu sagatavošana laboratorijā, Kvartošana", standard: "LVS EN 12697-28:2020" },
              { name: "Saistvielas saturs", standard: "LVS EN 12697-1:2020; p.1.7." },
              { name: "Granulometriskais sastāvs", standard: "LVS EN 12697-2:2025" },
              { name: "Paraugu sagatavošana ar Maršala kompaktoru", standard: "LVS EN 12697-30:2019" },
              { name: "Tilpumblīvums", standard: "LVS EN 12697-6:2020 p.9.3." },
              { name: "Tilpumblīvums", standard: "LVS EN 12697-6:2020 p.9.5." },
              { name: "Maksimālais blīvums", standard: "LVS EN 12697-5:2019 p.9.2." },
              { name: "Poru aprēķins", standard: "LVS EN 12697-8:2019" },
              { name: "Maršala stabilitāte, plūstamība", standard: "LVS EN 12697-34:2020" },
            ]
          },
          {
            title: "Izurbto paraugu testēšana",
            items: [
              { name: "Paraugu noņemšana objektā", standard: "ABS 2026; p 9.4, LVS EN 12697-27:2017, p.4.7." },
              { name: "Paraugu sagatavošana laboratorijā", standard: "" },
              { name: "Kārtu biezums", standard: "LVS EN 12696-36:2022 p.6.1." },
              { name: "Saistvielas saturs", standard: "LVS EN 12697-1:2020 p.1.7." },
              { name: "Tilpumblīvums", standard: "LVS EN 12697-6:2020 p.9.3." },
              { name: "Maksimālais blīvums", standard: "LVS EN 12697-5:2019 p.9.2." },
              { name: "Poru aprēķins", standard: "LVS EN 12697-8:2019" },
            ]
          }
        ]
      },
      mineralmateriali: {
        title: "Minerālmateriāli",
        subcategories: [
          {
            title: "Smalko minerālmateriālu testēšana (smilts, izsijas)",
            items: [
              { name: "Paraugu noņemšana objektā", standard: "LVS EN 932-1:1996 p.8.8." },
              { name: "Granulometriskais sastāvs", standard: "LVS EN 933-1:2012" },
              { name: "Daļiņu blīvums", standard: "LVS EN 1097-6:2022 p.A.4." },
              { name: "Smilts ekvivalents", standard: "LVS EN 933-8+A1:2015" },
              { name: "Plūšana koeficients", standard: "LVS EN 933-6:2023 p.8." },
              { name: "Metilēnzilais tests", standard: "LVS EN 933-9:2022" },
              { name: "Proktora blīvums pie optimālā mitruma (Ø 100 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Filtrācijas koeficients", standard: "ABS 2026; p.9.3" },
              { name: "Ūdens absorbcija", standard: "LVS EN 1097-6:2022 p.9. (0,063-4,0 mm)" },
              { name: "Irdenais bēruma blīvums", standard: "LVS EN 1097-3:1998, izņemot pielikumu A." },
              { name: "Ūdens saturs", standard: "LVS EN 1097-5:2008" },
              { name: "Organisko savienojumu satura noteikšana", standard: "ABS 2026; p 9.5" },
            ]
          },
          {
            title: "Rupjo minerālmateriālu testēšana (šķembas, maisījumi)",
            items: [
              { name: "Paraugu noņemšana objektā", standard: "LVS EN 932-1:1996 p.8.8." },
              { name: "Granulometriskais sastāvs", standard: "LVS EN 933-1:2012" },
              { name: "Daļiņu blīvums", standard: "LVS EN 1097-6:2022 p.A.4." },
              { name: "Formas indekss", standard: "LVS EN 933-4:2008" },
              { name: "Plākšņainības indekss", standard: "LVS EN 933-3:2012" },
              { name: "Drupināto un lauzto % attiecība", standard: "LVS EN 933-5:2023" },
              { name: "Losandželosas tests", standard: "LVS EN 1097-2:2020 p.5" },
              { name: "Losandželosas tests", standard: "LVS EN 1097-2:2020 p.A.2." },
              { name: "Losandželosas tests", standard: "LVS EN 1097-2:2020 p.B.1." },
              { name: "Losandželosas tests", standard: "LVS EN 1097-2:2020 pielikums G." },
              { name: "Nordiskais tests", standard: "LVS EN 1097-9:2014" },
              { name: "Magnija sulfāta tests", standard: "LVS EN 1367-2:2010" },
              { name: "Proktora blīvums pie optimālā mitruma (Ø 150 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Proktora blīvums pie optimālā mitruma (Ø 250 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Ūdens absorbcija", standard: "LVS EN 1097-6:2022 p.8. (4,0-31,5 mm)" },
              { name: "Irdenais bēruma blīvums", standard: "LVS EN 1097-5:2008, izņemot pielikumu A." },
              { name: "Ūdens saturs", standard: "LVS EN 1097-5:2008" },
            ]
          }
        ]
      },
      merijumi: {
        title: "Mērījumi objektā",
        subcategories: [{ title: "", items: [
          { name: "Plātnes slodzes tests", standard: "DIN 18134:2012" },
          { name: "Šķembu sablīvējums (Gredzena metode)", standard: "DIN 18125-2:2020 p.8.2." },
        ]}]
      },
      bitumens: {
        title: "Bitumens",
        subcategories: [{ title: "", items: [
          { name: "Bitumena paraugu ņemšana", standard: "LVS EN 58:2012 p.8.1.2." },
          { name: "Bitumena penetrācija", standard: "LVS EN 1426:2024" },
          { name: "Bitumena mīkstēšanas temperatūra", standard: "LVS EN 1427:2015" },
          { name: "Frass trausluma temperatūra", standard: "LVS EN 12593:2015" },
        ]}]
      }
    },
    en: {
      asfaltbetoni: {
        title: "Asphalt Mixtures",
        subcategories: [
          {
            title: "Asphalt Mixtures",
            items: [
              { name: "Sampling on site", standard: "LVS EN 12697-27:2017, Clause .4.1.,Clause. 4.3." },
              { name: "Laboratory sample preparation, quartering", standard: "LVS EN 12697-28:2020" },
              { name: "Binder content", standard: "LVS EN 12697-1:2020; Clause 1.7" },
              { name: "Particle size distribution", standard: "LVS EN 12697-2:2025" },
              { name: "Sample preparation using Marshall compactor", standard: "LVS EN 12697-30:2019" },
              { name: "Bulk density", standard: "LVS EN 12697-6:2020, Clause 9.3" },
              { name: "Bulk density", standard: "LVS EN 12697-6:2020, Clause 9.5" },
              { name: "Maximum density", standard: "LVS EN 12697-5:2019, Clause 9.2" },
              { name: "Void content calculation", standard: "LVS EN 12697-8:2019" },
              { name: "Marshall stability and flow", standard: "LVS EN 12697-34:2020" },
            ]
          },
          {
            title: "Testing of Core Samples",
            items: [
              { name: "Sampling on site", standard: "ABS 2026; Clause 9.4, LVS EN 12697-27:2017, Clause 4.7" },
              { name: "Laboratory sample preparation", standard: "" },
              { name: "Layer thickness", standard: "LVS EN 12696-36:2022, Clause 6.1" },
              { name: "Binder content", standard: "LVS EN 12697-1:2020, Clause 1.7" },
              { name: "Bulk density", standard: "LVS EN 12697-6:2020, Clause 9.3" },
              { name: "Maximum density", standard: "LVS EN 12697-5:2019, Clause 9.2" },
              { name: "Void content calculation", standard: "LVS EN 12697-8:2019" },
            ]
          }
        ]
      },
      mineralmateriali: {
        title: "Mineral Materials",
        subcategories: [
          {
            title: "Testing of Fine Aggregates (Sand, Screenings)",
            items: [
              { name: "Sampling on site", standard: "LVS EN 932-1:1996, Clause 8.8" },
              { name: "Particle size distribution", standard: "LVS EN 933-1:2012" },
              { name: "Particle density", standard: "LVS EN 1097-6:2022, Clause A.4" },
              { name: "Sand equivalent", standard: "LVS EN 933-8+A1:2015" },
              { name: "Flow coefficient", standard: "LVS EN 933-6:2023, Clause 8" },
              { name: "Methylene blue test", standard: "LVS EN 933-9:2022" },
              { name: "Proctor density at optimum moisture content (Ø 100 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Permeability coefficient", standard: "ABS 2026, Clause 9.3" },
              { name: "Water absorption", standard: "LVS EN 1097-6:2022, Clause 9 (0.063–4.0 mm)" },
              { name: "Loose bulk density", standard: "LVS EN 1097-3:1998, excluding Annex A" },
              { name: "Water content", standard: "LVS EN 1097-5:2008" },
              { name: "Determination of organic compound content", standard: "ABS 2026, Clause 9.5" },
            ]
          },
          {
            title: "Testing of Coarse Aggregates (Crushed Stone and Aggregate Mixtures)",
            items: [
              { name: "Sampling on site", standard: "LVS EN 932-1:1996, Clause 8.8" },
              { name: "Particle size distribution", standard: "LVS EN 933-1:2012" },
              { name: "Particle density", standard: "LVS EN 1097-6:2022, Clause A.4" },
              { name: "Shape index", standard: "LVS EN 933-4:2008" },
              { name: "Flakiness index", standard: "LVS EN 933-3:2012" },
              { name: "Percentage of crushed and broken particles", standard: "LVS EN 933-5:2023" },
              { name: "Los Angeles test", standard: "LVS EN 1097-2:2020, Clause 5" },
              { name: "Los Angeles test", standard: "LVS EN 1097-2:2020, Clause A.2" },
              { name: "Los Angeles test", standard: "LVS EN 1097-2:2020, Clause B.1" },
              { name: "Los Angeles test", standard: "LVS EN 1097-2:2020, Annex G" },
              { name: "Nordic test", standard: "LVS EN 1097-9:2014" },
              { name: "Magnesium sulfate test", standard: "LVS EN 1367-2:2010" },
              { name: "Proctor density at optimum moisture content (Ø 150 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Proctor density at optimum moisture content (Ø 250 mm)", standard: "LVS EN 13286-2:2011" },
              { name: "Water absorption", standard: "LVS EN 1097-6:2022, Clause 8 (4.0–31.5 mm)" },
              { name: "Loose bulk density", standard: "LVS EN 1097-5:2008, excluding Annex A" },
              { name: "Water content", standard: "LVS EN 1097-5:2008" },
            ]
          }
        ]
      },
      merijumi: {
        title: "Field Measurements",
        subcategories: [{ title: "", items: [
          { name: "Plate load test", standard: "DIN 18134:2012" },
          { name: "Compaction of crushed stone (Ring method)", standard: "DIN 18125-2:2020, Clause 8.2" },
        ]}]
      },
      bitumens: {
        title: "Bitumen",
        subcategories: [{ title: "", items: [
          { name: "Bitumen sampling", standard: "LVS EN 58:2012, Clause 8.1.2" },
          { name: "Bitumen penetration", standard: "LVS EN 1426:2024" },
          { name: "Bitumen softening point", standard: "LVS EN 1427:2015" },
          { name: "Fraass breaking point", standard: "LVS EN 12593:2015" },
        ]}]
      }
    }
  },
  nonAccredited: {
    lv: {
      asfaltbetoni: {
        title: "Asfaltbetoni",
        subcategories: [{
          title: "Asfaltbetona maisījumi",
          items: [
            { name: "Ūdensjutība", standard: "LVS EN 12697-12:2018" },
            { name: "Minerālo materiālu un bitumena savietojamība", standard: "ABS 2026; p 9.7" },
            { name: "Asfalta maisīšana laboratorijā", standard: "LVS EN 12697-35:2025" },
            { name: "Bitumena notece", standard: "LVS EN 12697-18:2017" },
            { name: "Bitumena atgūšana no asfalta masas ar rotācijas iztvaicētāju", standard: "LVS EN 12697-3:2019" },
            { name: "Paraugu sagatavošana ar veltņa blīvētāju", standard: "LVS EN 12697-33+A1:2023" },
            { name: "Riteņu sliežu veidošanās tests", standard: "LVS EN 12697-22+A1:2024" },
            { name: "Porasfalta parauga daļiņu zudums", standard: "LVS EN 12697-17:2017" },
            { name: "Bituminētā maisījuma parauga izmēru noteikšana", standard: "LVS EN 12697-29:2020" },
          ]
        }]
      },
      mineralmateriali: {
        title: "Minerālmateriāli",
        subcategories: [{ title: "", items: [
          { name: "Paraugu sagatavošana laboratorijā", standard: "LVS EN 932-2:1999" },
          { name: "Plasticitātes robeža", standard: "ASTM D 4318-05" },
          { name: "Plūšanas robeža", standard: "ASTM D 4318-05" },
          { name: "Plasticitātes indekss", standard: "ASTM D 4318-05" },
          { name: "Losandželosas tests", standard: "ABS 2026; p.9.6." },
          { name: "Hidrauliski saistītu maisījumu spiedes stiprības testēšanas metode", standard: "LVS EN 13286-41:2022" },
        ]}]
      },
      merijumi: {
        title: "Mērījumi objektā",
        subcategories: [{ title: "", items: [
          { name: "Minerālo materiālu sablīvējums", standard: "Iekārta TransTech SDG 200" },
          { name: "Šķembu sablīvējums (Smilts aizvietošanas metode)", standard: "BS 1377:9.daļa:1990" },
        ]}]
      },
      bitumens: {
        title: "Bitumens",
        subcategories: [{ title: "", items: [
          { name: "PMB bitumena paraugu izgatavošana (bitumena modificēšana)", standard: "" },
        ]}]
      }
    },
    en: {
      asfaltbetoni: {
        title: "Asphalt Mixtures",
        subcategories: [{
          title: "Asphalt Mixtures",
          items: [
            { name: "Water sensitivity", standard: "LVS EN 12697-12:2018" },
            { name: "Compatibility of mineral materials and bitumen", standard: "ABS 2026, Clause 9.7" },
            { name: "Laboratory asphalt mixing", standard: "LVS EN 12697-35:2025" },
            { name: "Binder drainage", standard: "LVS EN 12697-18:2017" },
            { name: "Recovery of bitumen from asphalt mixture using a rotary evaporator", standard: "LVS EN 12697-3:2019" },
            { name: "Sample preparation using roller compactor", standard: "LVS EN 12697-33+A1:2023" },
            { name: "Wheel tracking test", standard: "LVS EN 12697-22+A1:2024" },
            { name: "Particle loss of porous asphalt specimens", standard: "LVS EN 12697-17:2017" },
            { name: "Determination of the dimensions of bituminous specimen", standard: "LVS EN 12697-29:2020" },
          ]
        }]
      },
      mineralmateriali: {
        title: "Mineral Materials",
        subcategories: [{ title: "", items: [
          { name: "Laboratory sample preparation", standard: "LVS EN 932-2:1999" },
          { name: "Plastic limit", standard: "ASTM D 4318-05" },
          { name: "Liquid limit", standard: "ASTM D 4318-05" },
          { name: "Plasticity index", standard: "ASTM D 4318-05" },
          { name: "Los Angeles test", standard: "ABS 2026, Clause 9.6" },
          { name: "Compressive strength testing method for hydraulically bound mixtures", standard: "LVS EN 13286-41:2022" },
        ]}]
      },
      merijumi: {
        title: "Field Measurements",
        subcategories: [{ title: "", items: [
          { name: "Mineral material compaction testing", standard: "TransTech SDG 200" },
          { name: "Compaction of crushed stone (Sand replacement method)", standard: "BS 1377: Part 9:1990" },
        ]}]
      },
      bitumens: {
        title: "Bitumen",
        subcategories: [{ title: "", items: [
          { name: "Preparation of PMB Bitumen Samples (Bitumen Modification)", standard: "" },
        ]}]
      }
    }
  }
};

const NAV_ITEMS_BASE = [
  { key: "about", href: "#par-mums" },
  { key: "methods", href: "#metodes" },
  { key: "services", href: "#pakalpojumi" },
  { key: "laboratory", href: "#laboratorija" },
  { key: "contact", href: "#kontakti" },
];

// ─── Scroll Reveal ───
function useRevealChildren(threshold = 0.1) {
  const ref = useRef(null);
  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const children = container.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          children.forEach((child) => child.classList.add("visible"));
          observer.unobserve(container);
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(container);
    return () => observer.unobserve(container);
  }, [threshold]);
  return ref;
}

// ─── Smart Image (BUG FIX: accepts wrapperClassName to avoid breaking hero absolute layout) ───
function SmartImage({ imageKey, alt, className, wrapperClassName = "relative group" }) {
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { setSrc(`${API}/images/${imageKey}?v=1`); };
    img.onerror = () => { setSrc(FALLBACK_IMAGES[imageKey]); };
    img.src = `${API}/images/${imageKey}`;
  }, [imageKey]);

  const handleRegenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/images/generate`, { prompt: "", image_key: imageKey });
      if (res.data.success) setSrc(`${API}/images/${imageKey}?t=${Date.now()}`);
    } catch { /* ignore */ }
    finally { setGenerating(false); }
  };

  if (!src) return <div className={`${className} bg-[#E5DFD8] animate-pulse`} />;

  return (
    <div className={wrapperClassName}>
      <img
        src={src}
        alt={alt}
        className={`${className} img-load ${loaded ? "loaded" : ""}`}
        onLoad={() => setLoaded(true)}
      />
      <button
        onClick={handleRegenerate}
        disabled={generating}
        data-testid={`regenerate-${imageKey}`}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white p-2 rounded-full hover:bg-black/70 z-10"
        title="Pārveidot ar AI"
      >
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ─── Navigation ───
function Navigation() {
  const { lang, setLang, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const navItems = NAV_ITEMS_BASE.map(item => ({
    ...item,
    label: t.nav[item.key]
  }));

  useEffect(() => {
    const sectionIds = NAV_ITEMS_BASE.map((item) => item.href.slice(1));
    let animationFrame = null;

    const updateActiveSection = () => {
      const anchorOffset = 112;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const sections = sectionIds
        .map((id) => {
          const el = document.getElementById(id);
          if (!el) return null;

          const top = el.getBoundingClientRect().top + window.scrollY;
          return {
            id,
            top,
            trigger: Math.min(Math.max(0, top - anchorOffset), maxScroll),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.top - b.top);

      if (!sections.length) return;

      const hashSectionId = decodeURIComponent(window.location.hash.replace("#", ""));
      const hashSection = sections.find((section) => section.id === hashSectionId);
      if (hashSection && Math.abs(window.scrollY - hashSection.trigger) <= 8) {
        setActiveSection(hashSection.id);
        return;
      }

      let currentSection = "";
      for (const section of sections) {
        if (window.scrollY >= section.trigger - 1) {
          currentSection = section.id;
        } else {
          break;
        }
      }

      setActiveSection(currentSection);
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        updateActiveSection();
      });
    };

    updateActiveSection();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    window.addEventListener("hashchange", requestUpdate);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      window.removeEventListener("hashchange", requestUpdate);
    };
  }, []);

  // Custom breakpoint at 900px
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const checkWidth = () => setIsDesktop(window.innerWidth >= 900);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Language switcher component
  const LanguageSwitcher = ({ mobile = false }) => (
    <div className={`flex items-center gap-1 ${mobile ? '' : 'ml-2'}`}>
      <button
        onClick={() => setLang('lv')}
        data-testid="lang-lv"
        className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
          lang === 'lv' 
            ? 'bg-white/20 text-white' 
            : 'text-white/50 hover:text-white hover:bg-white/10'
        }`}
      >
        LV
      </button>
      <span className="text-white/30">|</span>
      <button
        onClick={() => setLang('en')}
        data-testid="lang-en"
        className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
          lang === 'en' 
            ? 'bg-white/20 text-white' 
            : 'text-white/50 hover:text-white hover:bg-white/10'
        }`}
      >
        EN
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop Navigation - 900px and above */}
      {isDesktop && (
        <nav data-testid="main-navigation" className="fixed z-50 top-6 left-1/2 -translate-x-1/2">
          <div className="dock-glass flex items-center gap-2 px-5 py-3">
            <a href="#" data-testid="nav-logo" className="flex items-center px-3 shrink-0">
              <img src={LOGO_WHITE_URL} alt="STRATA" className="h-12 w-auto max-w-[180px] object-contain object-left" />
            </a>
            <div className="w-[1px] h-7 bg-white/15 mx-2 shrink-0" />
            <div className="flex items-center gap-1 shrink-0">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} data-testid={`nav-link-${item.href.slice(1)}`}
                  className={`text-xs font-medium tracking-wide uppercase transition-all duration-300 px-4 py-2.5 rounded-full whitespace-nowrap
                    ${activeSection === item.href.slice(1) ? "bg-white/15 text-white" : "text-white/65 hover:text-white hover:bg-white/8"}`}>
                  {item.label}
                </a>
              ))}
            </div>
            <div className="w-[1px] h-7 bg-white/15 mx-2 shrink-0" />
            <LanguageSwitcher />
          </div>
        </nav>
      )}

      {/* Mobile Navigation - below 900px */}
      {!isDesktop && (
        <>
          {/* Mobile Header Bar */}
          <nav data-testid="mobile-navigation" className="fixed z-50 top-4 left-4 right-4">
            <div className="dock-glass flex items-center justify-between px-5 py-3.5">
              <a href="#" data-testid="nav-logo-mobile" className="shrink-0">
                <img src={LOGO_WHITE_URL} alt="STRATA" className="h-10 w-auto max-w-[140px] object-contain object-left" />
              </a>
              <div className="flex items-center gap-3">
                <LanguageSwitcher mobile />
                <button data-testid="mobile-menu-toggle" className="p-2 text-white shrink-0" onClick={() => setMobileOpen(!mobileOpen)}>
                  {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </nav>

          {/* Fullscreen Mobile Menu Overlay */}
          <div 
            className={`fixed inset-0 z-40 bg-[#045992] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            data-testid="mobile-menu-overlay"
          >
            <div className="flex flex-col justify-center items-center h-full px-8">
              <nav className="flex flex-col items-center gap-2">
                {navItems.map((item, index) => (
                  <a 
                    key={item.href} 
                    href={item.href} 
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-link-${item.href.slice(1)}`}
                    className={`text-2xl sm:text-3xl font-semibold tracking-wide uppercase py-4 transition-all duration-300
                      ${activeSection === item.href.slice(1) ? "text-white" : "text-white/60 hover:text-white"}`}
                    style={{ 
                      fontFamily: "Archivo, sans-serif",
                      transitionDelay: mobileOpen ? `${index * 50}ms` : '0ms',
                      transform: mobileOpen ? 'translateY(0)' : 'translateY(20px)',
                      opacity: mobileOpen ? 1 : 0
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              
              {/* Contact info at bottom */}
              <div className="absolute bottom-12 left-0 right-0 text-center">
                <a href="tel:+37126321959" className="text-white/60 hover:text-white text-sm tracking-wide transition-colors">
                  +371 26321959
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Hero (shorter, text at bottom like Aurova) ───
function HeroSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="hero-section" className="relative h-[60vh] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/hero-road.jpg"
          alt="Laboratory"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="hero-overlay absolute inset-0" />

      <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="hero-enter hero-enter-2 grid grid-cols-1 md:grid-cols-2 items-center gap-6 md:gap-12 mb-8 w-full">
          <div className="flex justify-start">
            <img
              src={LOGO_WHITE_URL}
              alt="STRATA"
              className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white" style={{ fontFamily: "Archivo, sans-serif" }}>
            {t.hero.subtitle}
          </h1>
        </div>
        <div className="hero-enter hero-enter-3 flex gap-4">
          <a href="#par-mums" data-testid="hero-cta-about" className="btn-pill btn-pill-accent">
            {t.hero.cta} <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#kontakti" data-testid="hero-cta-contact" className="btn-pill btn-pill-light">
            {t.hero.contact}
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── About (Aurova style: big text statement + button, then image row below) ───
function AboutSection() {
  const containerRef = useRevealChildren(0.1);
  const { t } = useLanguage();

  // STRATA rhomb icon marker used in bullet-style rows
  const StrataDiamond = ({ colored = true, size = "md" }) => {
    const sizeClasses = size === "sm" ? "w-5 h-5" : "w-6 h-6";
    return (
      <img
        src={RHOMBS_ICON_URL}
        alt=""
        aria-hidden="true"
        className={`${sizeClasses} shrink-0 object-contain ${colored ? "" : "grayscale opacity-45"}`}
      />
    );
  };

  return (
    <section id="par-mums" data-testid="about-section" className="bg-cream" ref={containerRef}>
      {/* Combined layout - All images left, all text right */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          
          {/* Left side - ALL 6 images vertically */}
          <div className="reveal delay-1 flex flex-col gap-4">
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about1.jpg" alt="Laboratory testing" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about2.jpg" alt="Bitumen samples" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about3.jpg" alt="Testing equipment" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about_gallery1.jpg" alt="Asphalt granules" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about_gallery2.jpg" alt="Bitumen testing" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[240px] overflow-hidden rounded-lg">
              <img src="/about_gallery3.jpg" alt="Laboratory equipment" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Right side - ALL text content */}
          <div className="flex flex-col">
            {/* Statement block - moved here */}
            <div className="reveal delay-2 mb-10">
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#1A2F2A]/50 mb-6">
                {t.about.label}
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug mb-8" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>
                {t.about.title}{" "}
                <span className="text-[#1A2F2A]/40">{t.about.titleFaded}</span>
              </h2>
              <a href="#metodes" className="btn-pill btn-pill-dark" data-testid="about-cta">
                {t.about.cta}
              </a>
            </div>

            {/* Laboratory services */}
            <div className="reveal delay-3 mb-10">
              <h3 className="text-2xl font-semibold text-[#1A2F2A] mb-6" style={{ fontFamily: "Archivo, sans-serif" }}>{t.about.labTitle}</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#045992]/10 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#045992] font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-[#1A2F2A]/80 mb-2">{t.about.labItem1}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#1A2F2A]/70"><StrataDiamond size="sm" /> {t.about.materials.sand}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#1A2F2A]/70"><StrataDiamond size="sm" /> {t.about.materials.gravel}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#1A2F2A]/70"><StrataDiamond size="sm" /> {t.about.materials.minerals}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#1A2F2A]/70"><StrataDiamond size="sm" /> {t.about.materials.bitumen}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#045992]/10 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-[#045992] font-bold text-sm">2</span>
                  </div>
                  <p className="text-[#1A2F2A]/80 pt-1">{t.about.labItem2}</p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#045992]/10 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-[#045992] font-bold text-sm">3</span>
                  </div>
                  <p className="text-[#1A2F2A]/80 pt-1">{t.about.labItem3}</p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#045992]/10 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-[#045992] font-bold text-sm">4</span>
                  </div>
                  <p className="text-[#1A2F2A]/80 pt-1">{t.about.labItem4}</p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="reveal delay-4 bg-white/60 border border-[#045992]/10 rounded-xl p-6 mb-10">
              <div className="flex items-baseline gap-3 mb-3">
                <p className="text-4xl font-bold text-[#045992]">70+</p>
                <p className="text-lg text-[#1A2F2A]/80">{t.about.stats}</p>
              </div>
              <p className="text-sm text-[#1A2F2A]/50">{t.about.statsDesc}</p>
            </div>

            {/* Locations */}
            <div className="reveal delay-5 mb-10">
              <p className="text-base font-semibold text-[#1A2F2A] mb-4">{t.about.locations}</p>
              <div className="flex flex-wrap gap-4 mb-4">
                <span className="inline-flex items-center gap-2 text-[#1A2F2A]/70"><StrataDiamond size="sm" /> Vangaži</span>
                <span className="inline-flex items-center gap-2 text-[#1A2F2A]/70"><StrataDiamond size="sm" /> Iecava</span>
                <span className="inline-flex items-center gap-2 text-[#1A2F2A]/70"><StrataDiamond size="sm" /> Limbaži</span>
                <span className="inline-flex items-center gap-2 text-[#1A2F2A]/70"><StrataDiamond size="sm" /> Daugavpils</span>
              </div>
              <p className="text-sm text-[#1A2F2A]/50">{t.about.locationsDesc}</p>
            </div>

            {/* Core values */}
            <div className="reveal delay-6">
              <h3 className="text-2xl font-semibold text-[#1A2F2A] mb-6" style={{ fontFamily: "Archivo, sans-serif" }}>{t.about.valuesTitle}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-4 bg-white/80 border border-[#045992]/10 rounded-xl p-4">
                  <StrataDiamond />
                  <span className="text-[#1A2F2A]/80">{t.about.value1}</span>
                </div>
                <div className="flex items-center gap-4 bg-white/80 border border-[#045992]/10 rounded-xl p-4">
                  <StrataDiamond />
                  <span className="text-[#1A2F2A]/80">{t.about.value2}</span>
                </div>
                <div className="flex items-center gap-4 bg-white/80 border border-[#045992]/10 rounded-xl p-4">
                  <StrataDiamond />
                  <span className="text-[#1A2F2A]/80">{t.about.value3}</span>
                </div>
                <div className="flex items-center gap-4 bg-white/80 border border-[#045992]/10 rounded-xl p-4">
                  <StrataDiamond />
                  <span className="text-[#1A2F2A]/80">{t.about.value4}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Quality (dark teal section) ───
function QualitySection() {
  const containerRef = useRevealChildren(0.1);
  const { t } = useLanguage();

  const cards = [
    { icon: <Beaker className="w-5 h-5" />, title: t.quality.card1Title, text: t.quality.card1Text },
    { icon: <FileCheck className="w-5 h-5" />, title: t.quality.card2Title, text: t.quality.card2Text },
    { icon: <Layers className="w-5 h-5" />, title: t.quality.card3Title, text: t.quality.card3Text },
  ];

  return (
    <section data-testid="quality-section" className="py-24 md:py-32 quality-glow" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          
          {/* Left side - Images */}
          <div className="reveal delay-1 flex flex-col gap-4">
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/methods1.jpg" alt="Asphalt samples" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/methods2.jpg" alt="Laboratory glassware" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/methods3.jpg" alt="Testing equipment" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Right side - Content */}
          <div className="flex flex-col justify-center">
            <p className="reveal delay-1 text-xs font-semibold tracking-[0.3em] uppercase text-[#F2EDE8]/40 mb-4">{t.quality.label}</p>
            <h2 className="reveal delay-2 text-3xl md:text-4xl font-semibold tracking-tight text-[#F2EDE8] mb-6" style={{ fontFamily: "Archivo, sans-serif" }}>
              {t.quality.title}
            </h2>
            <div className="space-y-5 text-base leading-relaxed text-[#F2EDE8]/60 mb-8">
              <p className="reveal delay-3">{t.quality.text1}</p>
              <p className="reveal delay-4">{t.quality.text2}</p>
            </div>

            <div className="flex flex-col gap-4">
              {cards.map((card, i) => (
                <div key={i} className={`reveal-right delay-${i + 5} flex items-start gap-4 p-5 rounded-lg bg-white/5 border border-white/8`}>
                  <div className="w-10 h-10 bg-[#045992]/30 flex items-center justify-center rounded-full text-white shrink-0">{card.icon}</div>
                  <div>
                    <h3 className="text-base font-semibold text-[#F2EDE8] mb-1" style={{ fontFamily: "Archivo, sans-serif" }}>{card.title}</h3>
                    <p className="text-sm text-[#F2EDE8]/50 leading-relaxed">{card.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Methods Category Component ───
function MethodCategory({ category, isAccredited = true }) {
  const [isOpen, setIsOpen] = useState(true);
  
  return (
    <div className="mb-6">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 w-full text-left mb-4 group"
      >
        <ChevronRight className={`w-5 h-5 text-[#045992] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        <h3 className="text-xl font-semibold text-[#1A2F2A]" style={{ fontFamily: "Archivo, sans-serif" }}>
          {category.title}
        </h3>
      </button>
      
      {isOpen && (
        <div className="flex flex-col gap-6 pl-8">
          {category.subcategories.map((sub, idx) => (
            <div key={idx}>
              {sub.title && (
                <h4 className="text-base font-semibold text-[#045992] mb-3">{sub.title}</h4>
              )}
              <div className="space-y-1">
                {sub.items.map((item, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 py-1.5">
                    <span className="text-[#1A2F2A]/70 text-[14px] sm:flex-1">{item.name}</span>
                    {item.standard && (
                      <span className="text-[#045992] font-mono text-[12px] break-words sm:shrink-0 sm:text-right">
                        {item.standard}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Methods ───
function MethodsSection() {
  const containerRef = useRevealChildren(0.05);
  const { lang, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('accredited');

  const accreditedMethods = METHODS_DATA.accredited[lang];
  const nonAccreditedMethods = METHODS_DATA.nonAccredited[lang];

  return (
    <section id="metodes" data-testid="methods-section" className="py-24 md:py-32 bg-cream" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <h2 className="reveal delay-1 text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>
            {t.methods.title}
          </h2>
          
          <div className="reveal delay-2 flex flex-wrap items-center gap-4 mb-6">
            <a
              href="/pdf/F54-Testesanas-metodes.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#045992] hover:text-[#034B7B] underline underline-offset-4 text-sm transition-colors"
              data-testid="methods-link"
            >
              {t.methods.link}
            </a>
            <a
              href="/pdf/Aplieciba_T-703_00_2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-pill-accent text-xs py-2 px-4"
              data-testid="accreditation-btn"
            >
              {t.methods.accreditationBtn}
            </a>
          </div>

          {/* Tab switcher */}
          <div className="reveal delay-3 flex gap-2">
            <button
              onClick={() => setActiveTab('accredited')}
              data-testid="tab-accredited"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeTab === 'accredited'
                  ? 'bg-gradient-to-r from-[#045992] to-[#034B7B] text-white shadow-md'
                  : 'bg-[#045992]/10 text-[#045992] border border-[#045992]/20 hover:bg-[#045992]/15'
              }`}
            >
              {t.methods.tabAccredited}
            </button>
            <button
              onClick={() => setActiveTab('non-accredited')}
              data-testid="tab-non-accredited"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeTab === 'non-accredited'
                  ? 'bg-gradient-to-r from-[#045992] to-[#034B7B] text-white shadow-md'
                  : 'bg-[#045992]/10 text-[#045992] border border-[#045992]/20 hover:bg-[#045992]/15'
              }`}
            >
              {t.methods.tabNonAccredited}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="reveal delay-4 bg-white/60 border border-[#045992]/10 rounded-xl p-6 md:p-8">
          {activeTab === 'accredited' ? (
            <div className="space-y-2">
              {Object.entries(accreditedMethods).map(([key, category]) => (
                <MethodCategory key={key} category={category} isAccredited={true} />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(nonAccreditedMethods).map(([key, category]) => (
                <MethodCategory key={key} category={category} isAccredited={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Services & Laboratories Combined ───
function ServicesSection() {
  const containerRef = useRevealChildren(0.1);
  const { t } = useLanguage();

  // STRATA rhomb icon marker for laboratory lists
  const StrataDiamond = ({ colored = true, size = "md" }) => {
    const sizeClasses = size === "sm" ? "w-5 h-5" : "w-6 h-6";
    return (
      <img
        src={RHOMBS_ICON_URL}
        alt=""
        aria-hidden="true"
        className={`${sizeClasses} shrink-0 object-contain ${colored ? "" : "grayscale opacity-45"}`}
      />
    );
  };

  const accreditedLabs = [
    { name: "Vangaži", address: t.services.addresses.vangazi, phone: "28345827" },
    { name: "Daugavpils", address: t.services.addresses.daugavpils, phone: "29406197" },
  ];

  const nonAccreditedLabs = [
    { name: "Iecava", address: t.services.addresses.iecava, phone: "28345827" },
    { name: "Limbaži", address: t.services.addresses.limbazi, phone: "28671076" },
  ];

  return (
    <section id="pakalpojumi" data-testid="services-section" className="py-24 md:py-32 bg-cream-alt" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          
          {/* Left side - 3 images vertically */}
          <div className="reveal delay-1 flex flex-col gap-4">
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/gallery1.jpg" alt="Sample measurement" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/gallery2.jpg" alt="Material quartering" className="w-full h-full object-cover" />
            </div>
            <div className="gallery-item h-[280px] overflow-hidden rounded-lg">
              <img src="/gallery3.jpg" alt="Sieving equipment" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Right side - Services + Laboratories text */}
          <div className="flex flex-col">
            
            {/* Services Section */}
            <div className="mb-12">
              <p className="reveal delay-1 text-xs font-semibold tracking-[0.3em] uppercase text-[#1A2F2A]/40 mb-4">{t.services.label}</p>
              <h2 className="reveal delay-2 text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>{t.services.title}</h2>
              <div className="space-y-4 text-base leading-relaxed text-[#1A2F2A]/60 mb-6">
                <p className="reveal delay-3">{t.services.text1}</p>
                <p className="reveal delay-4">{t.services.text2}</p>
              </div>
              
              <div className="space-y-3">
                <div className="reveal delay-5 bg-white/70 border border-[#1A2F2A]/8 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-[#1A2F2A] shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>{t.services.delivery}</h3>
                      <p className="text-xs text-[#1A2F2A]/50">{t.services.deliveryDesc}</p>
                    </div>
                  </div>
                </div>
                <div className="reveal delay-6 bg-white/70 border border-[#1A2F2A]/8 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-[#1A2F2A] shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>{t.services.onsite}</h3>
                      <p className="text-xs text-[#1A2F2A]/50">{t.services.onsiteDesc}</p>
                    </div>
                  </div>
                </div>
                <div className="reveal delay-7 bg-[#1A2F2A] text-[#F2EDE8] p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-[#045992]" />
                    <span className="font-semibold text-xs">{t.services.guarantee}</span>
                  </div>
                  <a href="mailto:laboratorija@strata.lv" className="text-[#F2EDE8]/70 hover:text-[#F2EDE8] underline underline-offset-4 text-xs">
                    laboratorija@strata.lv
                  </a>
                </div>
              </div>
            </div>

            {/* Laboratories Section */}
            <div id="laboratorija">
              <p className="reveal delay-8 text-xs font-semibold tracking-[0.3em] uppercase text-[#1A2F2A]/40 mb-4">{t.services.labsLabel}</p>
              <h2 className="reveal delay-9 text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: "Archivo, sans-serif", color: "#1A2F2A" }}>{t.services.labsTitle}</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Accredited */}
                <div className="reveal delay-10">
                  <h3 className="text-sm font-semibold text-[#045992] mb-4 flex items-center gap-2">
                    <StrataDiamond size="sm" />
                    {t.services.accredited}
                  </h3>
                  <div className="flex flex-col gap-3">
                    {accreditedLabs.map((lab, i) => (
                      <div key={i} className="bg-white/80 border border-[#045992]/15 rounded-lg p-4">
                        <h4 className="font-semibold text-[#1A2F2A] text-sm mb-1">{lab.name}</h4>
                        <p className="text-xs text-[#1A2F2A]/50 mb-1">{lab.address}</p>
                        <a href={`tel:+371${lab.phone}`} className="text-xs text-[#045992] flex items-center gap-1">
                          <Phone className="w-3 h-3" />{lab.phone}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Non-Accredited */}
                <div className="reveal delay-11">
                  <h3 className="text-sm font-semibold text-[#1A2F2A]/50 mb-4 flex items-center gap-2">
                    <StrataDiamond size="sm" colored={false} />
                    {t.services.nonAccredited}
                  </h3>
                  <div className="flex flex-col gap-3">
                    {nonAccreditedLabs.map((lab, i) => (
                      <div key={i} className="bg-white/60 border border-[#1A2F2A]/10 rounded-lg p-4">
                        <h4 className="font-semibold text-[#1A2F2A]/70 text-sm mb-1">{lab.name}</h4>
                        <p className="text-xs text-[#1A2F2A]/40 mb-1">{lab.address}</p>
                        <a href={`tel:+371${lab.phone}`} className="text-xs text-[#1A2F2A]/50 flex items-center gap-1">
                          <Phone className="w-3 h-3" />{lab.phone}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact & Footer (unified blue glow) ───
function ContactSection() {
  const containerRef = useRevealChildren(0.1);
  const { t } = useLanguage();

  return (
    <section id="kontakti" data-testid="contact-section" className="quality-glow" ref={containerRef}>
      {/* Contact Info */}
      <div className="pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 md:gap-16">
            {/* Main Contact */}
            <div>
              <p className="reveal delay-1 text-xs font-semibold tracking-[0.3em] uppercase text-[#F2EDE8]/40 mb-4">{t.contact.label}</p>
              <h2 className="reveal delay-2 text-3xl md:text-4xl font-semibold tracking-tight text-[#F2EDE8] mb-8" style={{ fontFamily: "Archivo, sans-serif" }}>{t.contact.title}</h2>
              {/*
              <a href="tel:+37126321959" className="reveal delay-3 inline-flex items-center gap-3 text-[#F2EDE8] text-2xl font-bold mb-6" data-testid="phone-main">
                <Phone className="w-6 h-6 text-white/60" />+371 26321959
              </a>
              */}
              <a href="mailto:laboratorija@strata.lv" className="reveal delay-4 flex items-center gap-3 text-[#F2EDE8]/60 hover:text-[#F2EDE8] transition-colors" data-testid="email-main">
                <Mail className="w-5 h-5 text-white/60" />laboratorija@strata.lv
              </a>
            </div>

            {/* Locations */}
            <div className="space-y-6">
              <div className="reveal delay-3">
                <h3 className="text-base font-semibold text-[#F2EDE8] mb-2 flex items-center gap-2" style={{ fontFamily: "Archivo, sans-serif" }}><MapPin className="w-4 h-4 text-white/60" />Vangaži</h3>
                <p className="text-[#F2EDE8]/40 text-sm pl-6">{t.services.addresses.vangazi}</p>
                <a href="tel:+37128345827" className="text-[#F2EDE8]/60 hover:text-[#F2EDE8] text-sm pl-6 flex items-center gap-2 mt-1" data-testid="phone-vangazi"><Phone className="w-3.5 h-3.5 text-white/50" />+371 28345827</a>
              </div>
              <div className="reveal delay-4">
                <h3 className="text-base font-semibold text-[#F2EDE8] mb-2 flex items-center gap-2" style={{ fontFamily: "Archivo, sans-serif" }}><MapPin className="w-4 h-4 text-white/60" />Daugavpils</h3>
                <p className="text-[#F2EDE8]/40 text-sm pl-6">{t.services.addresses.daugavpils}</p>
                <a href="tel:+37129406197" className="text-[#F2EDE8]/60 hover:text-[#F2EDE8] text-sm pl-6 flex items-center gap-2 mt-1" data-testid="phone-daugavpils"><Phone className="w-3.5 h-3.5 text-white/50" />+371 29406197</a>
              </div>
            </div>

            {/* Rekvizīti */}
            <div className="reveal delay-5">
              <h3 className="text-base font-semibold text-[#F2EDE8] mb-4" style={{ fontFamily: "Archivo, sans-serif" }}>{t.contact.details}</h3>
              <div className="space-y-1.5 text-sm text-[#F2EDE8]/40">
                <p>{t.contact.companyName}</p>
                <p>{t.contact.regNo} 40203626974</p>
                <p>{t.contact.address}</p>
                <p className="pt-2">{t.contact.bank} Luminor Bank AS Latvijas filiāle</p>
                <p>SWIFT: RIKOLV2X</p>
                <p>{t.contact.account} LV08RIKO0002930383929</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/StrataLogoGray.svg" alt="STRATA neatkarīga akreditēta laboratorija" className="h-9 md:h-11 w-auto opacity-50" data-testid="footer-logo" />
            <div className="flex gap-6 text-xs text-[#F2EDE8]/30">
              <span>{t.contact.cookies}</span>
              <a href="/pdf/Privatuma-politika.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#F2EDE8]/60 transition-colors">{t.contact.privacy}</a>
            </div>
            <p className="text-xs text-[#F2EDE8]/30">&copy; {new Date().getFullYear()} STRATA. {t.contact.rights}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  const [lang, setLang] = useState('lv');
  const t = TRANSLATIONS[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="min-h-screen">
        <Navigation />
        <HeroSection />
        <AboutSection />
        <QualitySection />
        <MethodsSection />
        <ServicesSection />
        <ContactSection />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
